import React, { Component, Fragment } from 'react';
import fp from 'lodash/fp';
import moment from 'moment';
import PropTypes from 'prop-types';
import { toastr } from 'react-redux-toastr';
import {
  Form,
  FormControl,
  ControlLabel,
  HelpBlock,
  Schema,
  SelectPicker,
  CheckPicker,
  DateRangePicker,
  Radio,
  Button,
  Checkbox,
  Icon,
  Grid,
  Row,
  Col
} from 'rsuite';
import { CurrentLocation } from '../../helpers/config';
import {
  PresetsPanel,
  PresetsPanelHeader,
  StyledFormGroup,
  StyledRadioGroup,
  StyledRow
} from './Elements';
import {
  REPORT_AS_VISIT,
  REPORT_AS_SERVICE,
  REPORT_BY_DUPLICATED,
  REPORT_BY_UNDUPLICATED,
  REPORT_AS_LABELS
} from '../../helpers/constants';
import { parseInt } from '../../helpers/utils';
import {
  dateRangesOptions,
  firstTimeByOptions,
  reportByOptions,
  resultByOptions,
  visitTrackingMethodOption
} from './form-options';
import { InlineHelpBlock } from '../widgets/FormElements';
import ReportTemplatesList from '../ReportTemplatesList';

const { NumberType, ArrayType, StringType } = Schema.Types;

const defaultFormValue = {
  dateRange: fp.get('0.value', dateRangesOptions),
  locations: [CurrentLocation],
  trackingMethods: [],
  groupBy: [],
  sumOrCountBy: [],
  firstTimeBy: null,
  reportType: null,
  reportAs: REPORT_AS_VISIT,
  resultBy: null,
  name: 'OAR Report',
  formValue: []
};

const SUM_OR_COUNT_BY_MAX_LENGTH = 20;

class OutreachAggSearchForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      formValue: defaultFormValue,
      formError: {},
      checkAllPrograms: false,
      indeterminatePrograms: false,
      showReportTemplates: false
    };

    this.formRef = React.createRef();
    this.handleCheckAllPrograms = this.handleCheckAllPrograms.bind(this);
  }

  componentDidMount() {
    const { onInit } = this.props;
    onInit();
  }

  get groupByMaxLength() {
    const { formValue } = this.state;
    return fp.get('resultBy', formValue) ? 2 : 3;
  }

  get formModel() {
    const { formValue } = this.state;
    return Schema.Model({
      dateRange: ArrayType().isRequired('This field is required'),
      locations: ArrayType()
        .isRequired('This field is required')
        .minLength(1, 'Please select at least 1.'),
      trackingMethods: ArrayType()
        .isRequired('This field is required')
        .minLength(1, 'Please select at least 1.'),
      groupBy: ArrayType().maxLength(
        this.groupByMaxLength,
        `Please select only up to ${this.groupByMaxLength}`
      ),
      sumOrCountBy: ArrayType().maxLength(
        SUM_OR_COUNT_BY_MAX_LENGTH,
        `Please select only up to ${SUM_OR_COUNT_BY_MAX_LENGTH}`
      ),
      firstTimeBy: !this.isReportTypeTotalOrUnique(formValue.reportType)
        ? NumberType().isRequired('This field is required')
        : NumberType(),
      reportType: NumberType().isRequired('This field is required'),
      resultBy: NumberType(),
      name: StringType().isRequired('This field is required')
    });
  }

  get selectedLocationsTrackingMethodOptions() {
    const { formValue } = this.state;
    const { trackingMethodOptions } = this.props;
    return fp.filter(
      ({ locationId }) =>
        fp.includes(parseInt(locationId), formValue.locations),
      trackingMethodOptions
    );
  }

  get groupByOptions() {
    const { groupByOptions } = this.props;
    const { formValue } = this.state;
    return this.isReportAsService(formValue.reportAs)
      ? [...groupByOptions, visitTrackingMethodOption]
      : groupByOptions;
  }

  isReportTypeTotalOrUnique = reportType =>
    reportType === REPORT_BY_DUPLICATED ||
    reportType === REPORT_BY_UNDUPLICATED;

  isReportAsService = reportAs => fp.isEqual(REPORT_AS_SERVICE, reportAs);

  isAllProgramsSelected = selectedTrackingMethods => {
    const { trackingMethodOptions } = this.props;
    return fp.size(selectedTrackingMethods) === fp.size(trackingMethodOptions);
  };

  handleFormChange = data => {
    const { firstTimeBy, reportAs, reportType, groupBy } = data;
    const isReportTypeTotalOrUnique = this.isReportTypeTotalOrUnique(
      reportType
    );
    const formValue = {
      ...data,
      firstTimeBy: isReportTypeTotalOrUnique ? null : firstTimeBy,
      groupBy: this.isReportAsService(reportAs)
        ? groupBy
        : fp.without([visitTrackingMethodOption.value], groupBy)
    };

    const hasSelectedPrograms = fp.size(formValue.trackingMethods) > 0;
    const isAllProgramsSelected = this.isAllProgramsSelected(
      formValue.trackingMethods
    );
    this.setState(
      {
        formValue,
        checkAllPrograms: hasSelectedPrograms && isAllProgramsSelected,
        indeterminatePrograms: hasSelectedPrograms && !isAllProgramsSelected
      },
      () => {
        if (isReportTypeTotalOrUnique) this.formRef.current.check();
      }
    );
  };

  toggleShowTemplates = () => {
    const { showReportTemplates } = this.state;
    this.setState({ showReportTemplates: !showReportTemplates });
  };

  onSelectReportTemplate = ({ file, fileName }) => {
    const { formValue } = this.state;
    const { dateRange } = formValue;
    const {
      reportType,
      reportAs,
      reportBy,
      resultBy,
      firstTimeBy,
      trackingMethods,
      ...restFile
    } = file;

    const locations = fp.getOr(formValue.locations, 'locations', file);

    const updatedQuery = {
      ...restFile,
      dateRange,
      reportType: parseInt(reportType),
      reportAs: parseInt(reportAs),
      reportBy: reportBy ? parseInt(reportBy) : null,
      resultBy: resultBy ? parseInt(resultBy) : null,
      firstTimeBy: firstTimeBy ? parseInt(firstTimeBy) : null,
      locations: fp.map(parseInt, locations),
      trackingMethods: fp.map(parseInt, trackingMethods),
      name: fileName
    };
    this.setState({ formValue: updatedQuery, showReportTemplates: false });
  };

  clearForm = () => {
    this.setState({ formValue: defaultFormValue, showReportTemplates: false });
  };

  handleSubmit = () => {
    if (!this.formRef.current.check()) {
      toastr.error('Please check the form for some field errors.');
      return;
    }
    const { onSubmit } = this.props;
    const { formValue } = this.state;
    onSubmit({
      ...formValue,
      dateRange: fp.map(moment, formValue.dateRange)
    });
  };

  handleCheckAllPrograms(_, checked) {
    const { formValue } = this.state;
    const { trackingMethodOptions } = this.props;
    const trackingMethods = checked
      ? fp.map('value', trackingMethodOptions)
      : [];
    this.setState({
      formValue: { ...formValue, trackingMethods },
      checkAllPrograms: checked,
      indeterminatePrograms: false
    });
  }

  renderCheckAllPrograms = () => {
    const { checkAllPrograms, indeterminatePrograms } = this.state;
    return (
      <Checkbox
        inline
        indeterminate={indeterminatePrograms}
        checked={checkAllPrograms}
        onChange={this.handleCheckAllPrograms}
      >
        Check all
      </Checkbox>
    );
  };

  renderActionButtons = () => {
    const { isSaving } = this.props;
    const { formValue } = this.state;
    const isEqualFormValue = fp.isEqual(formValue, defaultFormValue);
    return (
      <Row style={{ marginBottom: 20 }}>
        <Col xs={24}>
          <Button
            appearance="primary"
            size="sm"
            onClick={this.handleSubmit}
            style={{ marginRight: 10 }}
            disabled={isSaving}
          >
            <Icon icon="search" />
            {isSaving ? 'Searching...' : 'Search'}
          </Button>
          <Button
            appearance="default"
            size="sm"
            onClick={this.clearForm}
            disabled={isEqualFormValue || isSaving}
            style={{ marginRight: 10 }}
          >
            <Icon icon="refresh" /> Reset
          </Button>
        </Col>
      </Row>
    );
  };

  renderReportTemplates = () => {
    const { templates, isFetchingTemplates } = this.props;
    const { showReportTemplates, formValue } = this.state;
    const headerLabel = formValue.name
      ? `Preset: ${formValue.name}`
      : 'Select Preset';
    return (
      <PresetsPanel
        shaded
        collapsible
        expanded={showReportTemplates}
        header={
          <PresetsPanelHeader onClick={this.toggleShowTemplates}>
            {headerLabel}
          </PresetsPanelHeader>
        }
      >
        <ReportTemplatesList
          data={templates}
          loading={isFetchingTemplates}
          onSelect={this.onSelectReportTemplate}
          autoHeight={false}
          height={500}
        />
      </PresetsPanel>
    );
  };

  render() {
    const { sumOrCountByOptions, locationOptions } = this.props;
    const { formValue, formError } = this.state;

    return (
      <Fragment>
        <Form
          model={this.formModel}
          ref={this.formRef}
          formValue={formValue}
          onChange={this.handleFormChange}
          onCheck={error => {
            this.setState({ formError: error });
          }}
        >
          <Grid fluid>
            {this.renderActionButtons()}
            {this.renderReportTemplates()}
            <Row componentClass={StyledRow}>
              <Col xs={24} md={6} style={{ marginBottom: 5 }}>
                <StyledFormGroup>
                  <ControlLabel style={{ fontSize: 'initial' }}>
                    Select Date From - To*
                    <InlineHelpBlock tooltip>
                      Select the date range you are reporting for and click
                      &quot;OK&quot;
                    </InlineHelpBlock>
                  </ControlLabel>
                  <FormControl
                    style={{ width: 250 }}
                    placeholder="Select date range"
                    name="dateRange"
                    defaultValue={[]}
                    accepter={DateRangePicker}
                    ranges={dateRangesOptions}
                    errorMessage={formError.dateRange}
                  />
                  <HelpBlock>
                    <small>Default by Last Month</small>
                  </HelpBlock>
                </StyledFormGroup>
              </Col>
              <Col xs={24} md={18}>
                Set the Date Range for your report Start by choosing the From -
                To dates you would like to work with. Once you click on the date
                range box, you can select your dates by using the calendars, or
                by choosing one of the fast select options at the bottom of the
                field. To work with a single day, just double click on the day.
                All entries will be locked in when you click <strong>OK</strong>
                .
              </Col>
            </Row>

            <Row componentClass={StyledRow}>
              <Col xs={24} md={6}>
                <StyledFormGroup>
                  <ControlLabel style={{ fontSize: 'initial' }}>
                    Locations*
                    <InlineHelpBlock tooltip>
                      Select one or multiple locations.
                    </InlineHelpBlock>
                  </ControlLabel>
                  <FormControl
                    style={{ width: 250 }}
                    placeholder="Locations"
                    name="locations"
                    accepter={CheckPicker}
                    data={locationOptions}
                    errorMessage={formError.locations}
                  />
                </StyledFormGroup>
              </Col>
              <Col xs={24} md={18}>
                Choose the location(s) you wish to include in your report
                <br />
                <ul>
                  <li>
                    <strong>Single location domains: </strong>
                    Will only see one (1) location. Be sure it is checked.
                  </li>
                  <li>
                    <strong>Multi-location domains: </strong>
                    Will see all of the locations in this list. Choose one or
                    more locations to include in your report. Adding more than
                    one location here will provide aggregate data between all
                    locations added.
                  </li>
                </ul>
              </Col>
            </Row>

            <Row componentClass={StyledRow}>
              <Col xs={24} md={6}>
                <StyledFormGroup>
                  <ControlLabel style={{ fontSize: 'initial' }}>
                    Tracking Methods*
                    <InlineHelpBlock tooltip>
                      Select the Tracking Methods you are reporting on.
                    </InlineHelpBlock>
                  </ControlLabel>
                  <FormControl
                    style={{ width: 250 }}
                    disabled={fp.isEmpty(formValue.locations)}
                    placeholder="Tracking Methods"
                    name="trackingMethods"
                    groupBy="location"
                    accepter={CheckPicker}
                    data={this.selectedLocationsTrackingMethodOptions}
                    errorMessage={formError.trackingMethods}
                    renderExtraFooter={this.renderCheckAllPrograms}
                  />
                </StyledFormGroup>
              </Col>
              <Col xs={24} md={18}>
                Choose the Tracking Method(s) to include in this report.
                <br />
                You can select several tracking methods at the same time or
                choose just one. To make it easy to select all of the tracking
                methods in the report, there is a ‘Check All’ box located at the
                bottom of the list.
                <ul>
                  <li>
                    <strong>Choosing One Tracking Method: </strong>
                    Reports out data specifically relating to the tracking
                    method chosen. If I wanted a TEFAP only report, only choose
                    the TEFAP Tracking Method.
                  </li>
                  <li>
                    <strong>Choosing Multiple Tracking Methods: </strong>
                    Will report out the data from all chosen Tracking into into
                    an aggregated report.
                  </li>
                </ul>
              </Col>
            </Row>

            <Row componentClass={StyledRow}>
              <Col xs={24} md={6}>
                <StyledFormGroup>
                  <ControlLabel style={{ fontSize: 'initial' }}>
                    Report Type*
                    <InlineHelpBlock tooltip>
                      Select the type of report you want to run.
                    </InlineHelpBlock>
                  </ControlLabel>
                  <FormControl
                    style={{ width: 250 }}
                    placeholder="Reports"
                    name="reportType"
                    accepter={SelectPicker}
                    searchable={false}
                    data={reportByOptions}
                    errorMessage={formError.reportType}
                  />
                </StyledFormGroup>
              </Col>
              <Col xs={24} md={18}>
                There are four report type options. You may only run one (1)
                report type at a time.
                <ul>
                  <li>
                    <strong>New: </strong>
                    Provides a report that only shows the results for that are
                    considered new within the date range set for this report.
                    What determines a new visit is configured in the ‘First Time
                    By’ selector.
                  </li>
                  <li>
                    <strong>Returning: </strong>
                    Provides a report that only shows the results for visits
                    that are considered &lsquo;not new&rsquo; but are returning.
                    What returning. What determines a returning visit is
                    configured &lsquo;First Time By&rsquo; selector.
                  </li>
                  <li>
                    <strong>Total: </strong>
                    Can also be called duplicated. Total will include all visits
                    in the selected date range for the report.
                  </li>
                  <li>
                    <strong>Unique: </strong>
                    Can also be called unduplicated. Unique will provide a data
                    set result that only reports on the <i>first visit</i> for
                    each client in the selected time period. Caution: not Unique
                    with First Time!
                  </li>
                </ul>
              </Col>
            </Row>

            <Row componentClass={StyledRow}>
              <Col xs={6}>
                <StyledFormGroup>
                  <ControlLabel style={{ fontSize: 'initial' }}>
                    First Time By
                    <InlineHelpBlock tooltip>
                      Select the type of “New By” comparison needed.
                    </InlineHelpBlock>
                  </ControlLabel>
                  <FormControl
                    style={{ width: 250 }}
                    placeholder="Date Grouping"
                    name="firstTimeBy"
                    accepter={SelectPicker}
                    searchable={false}
                    data={firstTimeByOptions}
                    disabled={this.isReportTypeTotalOrUnique(
                      formValue.reportType
                    )}
                    errorMessage={formError.firstTimeBy}
                  />
                </StyledFormGroup>
              </Col>
              <Col xs={24} md={18}>
                <strong>First Time By </strong>
                is the partner to the &lsquo;Report Type&rsquo; selector. It is
                used to test the selected visits to determine if a visit is new
                or not new. This works by comparing the visits within the date
                range chosen and determines if the visits for each household are
                new visits or returning visits. There are four (4) options.
                <ul>
                  <li>
                    <strong>New this Calendar Year: </strong>
                    Tests the selected visits against all of the visits made
                    this calendar year.
                  </li>
                  <li>
                    <strong>New This Fiscal Year: </strong>
                    Tests the selected visits against all of the visits made
                    during the fiscal year set in settings &gt; settings &gt;
                    general.
                  </li>
                  <li>
                    <strong>New This Month: </strong>
                    Tests the selected visits against the visits within selected
                    month.
                  </li>
                  <li>
                    <strong>New Ever: </strong>
                    Tests the selected visits against all visits ever recorded.
                  </li>
                  <li>
                    <strong>New By Date Range: </strong>
                    Tests the selected visits against the selected date range.
                  </li>
                </ul>
              </Col>
            </Row>

            <Row componentClass={StyledRow}>
              <Col xs={24} md={6}>
                <StyledFormGroup>
                  <ControlLabel style={{ fontSize: 'initial' }}>
                    Show Result for:
                    <InlineHelpBlock tooltip>
                      Choose what constitutes a visit, visit = attendance or
                      visit = tracking method.
                    </InlineHelpBlock>
                  </ControlLabel>
                  <FormControl
                    style={{ width: '140px', textAlign: 'left' }}
                    name="reportAs"
                    accepter={StyledRadioGroup}
                    errorMessage={formError.reportAs}
                  >
                    <Radio value={REPORT_AS_VISIT}>
                      {REPORT_AS_LABELS[REPORT_AS_VISIT]}
                    </Radio>
                    <Radio value={REPORT_AS_SERVICE}>
                      {REPORT_AS_LABELS[REPORT_AS_SERVICE]}
                    </Radio>
                  </FormControl>
                </StyledFormGroup>
              </Col>
              <Col xs={24} md={18}>
                <strong>Show Result For</strong> tells the report what a visit
                is. There are two options: Physical Visit (attendance - commonly
                used) and Tracking Method Visit (rarely used).
                <ul>
                  <li>
                    <strong>Physical Visit </strong>
                    (attendance) considers all services received during a visit
                    as one (1) visit. An example is John comes in and receives
                    food and clothing. The physical visit will count both
                    services as only one (1) visit and outputs results for one
                    (1) visit.
                  </li>
                  <li>
                    <strong>Tracking Method Visit </strong>
                    considers each tracking method received during a visit as
                    separate visits. An example is John comes in and receives
                    food and clothing. The program visit will count both
                    services as their own visits and outputs results for two (2)
                    visits. This option is required if pivoting on Tracking
                    Method.
                  </li>
                </ul>
              </Col>
            </Row>

            <Row componentClass={StyledRow}>
              <Col xs={24} md={6}>
                <StyledFormGroup>
                  <ControlLabel style={{ fontSize: 'initial' }}>
                    Pivot On
                    <InlineHelpBlock tooltip>
                      Select the fields to divide the report results.
                    </InlineHelpBlock>
                  </ControlLabel>
                  <FormControl
                    style={{ width: 250 }}
                    placeholder="Columns"
                    name="groupBy"
                    accepter={CheckPicker}
                    groupBy="type"
                    data={this.groupByOptions}
                    errorMessage={formError.groupBy}
                  />
                  <HelpBlock>
                    <small>Max of {this.groupByMaxLength} selection</small>
                  </HelpBlock>
                </StyledFormGroup>
              </Col>
              <Col xs={24} md={18}>
                <strong>Pivot On </strong>
                columns allow you to add a field(s) to divide the report
                results. An example of this would be a Yes/No field. The results
                presented will show both Yes and No data sets. You may choose
                from Guest record fields and/or Visit fields. The Pivot On field
                is not required, however the maximum number of fields you can
                pivot on at any one time is three (3). Common pivots include zip
                code, household total, disabled and veteran.
              </Col>
            </Row>

            <Row componentClass={StyledRow}>
              <Col xs={24} md={6}>
                <StyledFormGroup>
                  <ControlLabel style={{ fontSize: 'initial' }}>
                    Sum/Count By
                    <InlineHelpBlock tooltip>
                      Select the fields you desire to see additional data for.
                    </InlineHelpBlock>
                  </ControlLabel>
                  <FormControl
                    style={{ width: 250 }}
                    placeholder="Columns"
                    name="sumOrCountBy"
                    accepter={CheckPicker}
                    groupBy="type"
                    data={sumOrCountByOptions}
                    errorMessage={formError.sumOrCountBy}
                  />
                  <HelpBlock>
                    <small>Max of {SUM_OR_COUNT_BY_MAX_LENGTH} selection</small>
                  </HelpBlock>
                </StyledFormGroup>
              </Col>
              <Col xs={24} md={18}>
                The
                <strong> Sum/Count By </strong>
                will sum the values for Number fields and will count how many
                times a value is chosen for Yes/No fields and Select box fields.
                There is a maximum of twenty (20) fields that can be included in
                a report.
                <ul>
                  <li>
                    <strong>Sum </strong>
                    will auto sum the field values for any field that numbers.
                    An example of this would be weight, household total
                    household total or age groups.
                  </li>
                  <li>
                    <strong>Count By </strong>
                    will total the number of times each option is present in the
                    data set provided. An example would be how many Yes or No
                    results.
                  </li>
                </ul>
              </Col>
            </Row>

            <Row componentClass={StyledRow}>
              <Col xs={24} md={6}>
                <StyledFormGroup>
                  <ControlLabel style={{ fontSize: 'initial' }}>
                    Result By:
                    <InlineHelpBlock tooltip>
                      Group result by Month, Week or Day
                    </InlineHelpBlock>
                  </ControlLabel>
                  <FormControl
                    style={{ width: 250 }}
                    placeholder="Result by"
                    name="resultBy"
                    accepter={SelectPicker}
                    data={resultByOptions}
                    errorMessage={formError.resultBy}
                  />
                </StyledFormGroup>
              </Col>
              <Col xs={24} md={18}>
                Allows you to divide the results into smaller subsets, Day,
                Week, Month or Year. If you do not choose any of these options
                your result will be based on the date range set in the ‘Select
                Date From – To’ selector.
              </Col>
            </Row>

            <Row componentClass={StyledRow}>
              <Col xs={24} md={6}>
                <StyledFormGroup>
                  <ControlLabel style={{ fontSize: 'initial' }}>
                    Report Name*
                    <InlineHelpBlock tooltip>
                      Serves as your Report Name.
                    </InlineHelpBlock>
                  </ControlLabel>
                  <FormControl
                    style={{ width: 250 }}
                    placeholder="Report Name"
                    name="name"
                    errorMessage={formError.name}
                  />
                </StyledFormGroup>
              </Col>
              <Col xs={24} md={18}>
                Use the Report Name field to identify one report from another in
                the History Page. The name field will take on the Preset name if
                a Preset is used. The field is editable as desired.
              </Col>
            </Row>
          </Grid>
          {this.renderActionButtons()}
        </Form>
      </Fragment>
    );
  }
}

OutreachAggSearchForm.propTypes = {
  onInit: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSaving: PropTypes.bool.isRequired,
  isFetchingTemplates: PropTypes.bool.isRequired,
  templates: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  groupByOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  sumOrCountByOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  locationOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  trackingMethodOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired
};

export default OutreachAggSearchForm;
